import React, { useState, useEffect } from 'react';
import { FiClock, FiCalendar, FiActivity, FiZap } from 'react-icons/fi';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { parentAnalyticsAPI } from '../../../api/parentEndpoints';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { showToast } from '../../../components/common/Toaster';
import { useTheme } from '../../../contexts/ThemeContext';
import { safeParseFloat, safeToFixed } from './utils';
import {
  Section,
  SectionTitle,
  Grid,
  Card,
  StatCard,
  StatLabel,
  StatValue,
  StatDescription,
  Badge,
  HeatmapContainer,
  HeatmapDayLabels,
  HeatmapDayLabel,
  HeatmapMonths,
  HeatmapMonth,
  HeatmapMonthLabel,
  HeatmapWeeks,
  HeatmapWeek,
  HeatmapCell,
  HeatmapLegend,
  HeatmapLegendLabel,
  HeatmapLegendSwatch,
  ChartContainer,
  LoadingContainer,
  EmptyState,
} from '../Analytics.styles';

const EngagementMetrics = ({ studentId, days = 30 }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [studentId, days]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await parentAnalyticsAPI.getEngagementMetrics(studentId, { days });
      setData(response.data.engagement_metrics);
    } catch (error) {
      showToast('error', 'Failed to load engagement metrics');
      console.error('Error fetching engagement metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHeatmapColor = (intensity) => {
    if (intensity === 0) return theme.colors.backgroundTertiary;
    if (intensity <= 0.25) return '#86EFAC';
    if (intensity <= 0.5) return '#4ADE80';
    if (intensity <= 0.75) return '#22C55E';
    return '#16A34A';
  };

  const prepareTimeDistributionData = () => {
    if (!data || !data.time_distribution || !data.time_distribution.by_subject) return [];
    
    return data.time_distribution.by_subject.map((subject) => ({
      name: subject.subject,
      hours: safeParseFloat(subject.hours),
      percentage: safeParseFloat(subject.percentage),
    }));
  };

  const prepareSessionData = () => {
    if (!data || !data.session_analysis || !data.session_analysis.daily_sessions) return [];
    
    return data.session_analysis.daily_sessions.map((session) => ({
      date: new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      minutes: safeParseFloat(session.duration_minutes),
      videos: safeParseFloat(session.video_count),
    }));
  };

  const preparePeakTimesData = () => {
    if (!data || !data.peak_times) return [];
    
    return data.peak_times.map((peak) => ({
      hour: peak.time_label,
      activity: safeParseFloat(peak.activity_count),
    }));
  };

  const prepareCompletionData = () => {
    if (!data || !data.completion_rates) return [];
    
    return data.completion_rates.map((rate) => ({
      name: rate.type.charAt(0).toUpperCase() + rate.type.slice(1),
      completed: safeParseFloat(rate.completed),
      total: safeParseFloat(rate.total),
      rate: safeParseFloat(rate.completion_rate),
    }));
  };

  const prepareHeatmapData = () => {
    if (!data || !data.heatmap_data) return [];
    
    // Get last 84 days (12 weeks) and organize by week
    const heatmapData = data.heatmap_data.slice(-84);
    const weeks = [];
    
    for (let i = 0; i < heatmapData.length; i += 7) {
      weeks.push(heatmapData.slice(i, i + 7));
    }
    
    return weeks;
  };

  const prepareHeatmapByMonth = () => {
    if (!data || !data.heatmap_data) return [];
    
    // Get last 84 days (12 weeks)
    const heatmapData = data.heatmap_data.slice(-84);
    
    // Group by month
    const monthsMap = new Map();
    
    heatmapData.forEach((day) => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          monthName,
          weeks: [],
          currentWeek: [],
        });
      }
      
      const monthData = monthsMap.get(monthKey);
      monthData.currentWeek.push(day);
      
      // Complete week (7 days) or last day of month
      if (monthData.currentWeek.length === 7) {
        monthData.weeks.push(monthData.currentWeek);
        monthData.currentWeek = [];
      }
    });
    
    // Add remaining days as partial week
    monthsMap.forEach((monthData) => {
      if (monthData.currentWeek.length > 0) {
        // Pad with empty days to complete the week
        while (monthData.currentWeek.length < 7) {
          monthData.currentWeek.push(null);
        }
        monthData.weeks.push(monthData.currentWeek);
      }
      delete monthData.currentWeek;
    });
    
    // Convert to array and reverse to show most recent month on the right
    return Array.from(monthsMap.values()).reverse();
  };

  const COLORS = [
    theme.colors.primary,
    theme.colors.secondary,
    theme.colors.accent,
    theme.colors.info,
    theme.colors.success,
    '#8B5CF6',
    '#EC4899',
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: theme.colors.surface,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: theme.shadows.lg,
        }}>
          <p style={{ margin: 0, fontWeight: 600, color: theme.colors.textPrimary }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: '4px 0 0 0', color: entry.color, fontSize: '14px' }}>
              {entry.name}: {typeof entry.value === 'number' ? safeToFixed(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner size="large" text="Loading engagement data..." />
      </LoadingContainer>
    );
  }

  if (!data) {
    return (
      <EmptyState>
        <FiActivity size={64} />
        <h3>No Engagement Data</h3>
        <p>Engagement metrics will appear once learning activity begins</p>
      </EmptyState>
    );
  }

  return (
    <>
      {/* Streaks & Activity */}
      <Section>
        <SectionTitle>
          <FiZap />
          Activity Streaks
        </SectionTitle>
        <Grid minWidth="250px">
          <StatCard>
            <StatLabel>Current Streak</StatLabel>
            <StatValue style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '48px' }}>🔥</span>
              {data.streaks.current_streak} days
            </StatValue>
            <StatDescription>
              {data.streaks.current_streak >= 7 ? '🎉 Amazing!' : 'Keep going!'}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Longest Streak</StatLabel>
            <StatValue color={theme.colors.accent}>
              {data.streaks.longest_streak} days
            </StatValue>
            <StatDescription>
              {data.streaks.longest_streak >= 30 ? '🏆 Outstanding!' : 'Personal best'}
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Active Days</StatLabel>
            <StatValue color={theme.colors.info}>
              {data.streaks.active_days}
            </StatValue>
            <StatDescription>
              out of {data.streaks.total_days} days<br />
              ({safeToFixed(((data.streaks.active_days / data.streaks.total_days) * 100), 0)}% activity rate)
            </StatDescription>
          </StatCard>

          <StatCard>
            <StatLabel>Total Study Time</StatLabel>
            <StatValue color={theme.colors.success}>
              {safeToFixed(data.time_distribution.total_hours)}h
            </StatValue>
            <StatDescription>
              in the last {data.period_days} days
            </StatDescription>
          </StatCard>
        </Grid>
      </Section>

      {/* Activity Heatmap */}
      {data.heatmap_data && data.heatmap_data.length > 0 && (
        <Section>
          <SectionTitle>
            <FiCalendar />
            Activity Heatmap
          </SectionTitle>
          <Card>
            <div style={{ marginBottom: '24px' }}>
              <p style={{ 
                color: theme.colors.textSecondary, 
                marginBottom: '16px', 
                fontSize: '14px',
                lineHeight: '1.5' 
              }}>
                Daily learning activity over the past 12 weeks. Each cell represents a day - hover to see details.
              </p>
              <HeatmapLegend>
                <HeatmapLegendLabel>Less</HeatmapLegendLabel>
                {[0, 0.25, 0.5, 0.75, 1].map((intensity, idx) => (
                  <HeatmapLegendSwatch
                    key={idx}
                    color={getHeatmapColor(intensity)}
                  />
                ))}
                <HeatmapLegendLabel>More Active</HeatmapLegendLabel>
              </HeatmapLegend>
            </div>
            
            <HeatmapContainer>
              {/* Day Labels */}
              <HeatmapDayLabels>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
                  <HeatmapDayLabel key={idx}>{day}</HeatmapDayLabel>
                ))}
              </HeatmapDayLabels>
              
              {/* Months with Weeks */}
              <HeatmapMonths>
                {prepareHeatmapByMonth().map((month, monthIdx) => (
                  <HeatmapMonth key={monthIdx}>
                    <HeatmapMonthLabel>{month.monthName}</HeatmapMonthLabel>
                    <HeatmapWeeks>
                      {month.weeks.map((week, weekIdx) => (
                        <HeatmapWeek key={weekIdx}>
                          {week.map((day, dayIdx) => (
                            <HeatmapCell
                              key={`${monthIdx}-${weekIdx}-${dayIdx}`}
                              color={day ? getHeatmapColor(day.intensity) : theme.colors.backgroundTertiary + '20'}
                              tooltip={day ? `${new Date(day.date).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                weekday: 'short'
                              })}: ${day.activity_count} ${day.activity_count === 1 ? 'activity' : 'activities'}` : 'No data'}
                            />
                          ))}
                        </HeatmapWeek>
                      ))}
                    </HeatmapWeeks>
                  </HeatmapMonth>
                ))}
              </HeatmapMonths>
            </HeatmapContainer>
            
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: `linear-gradient(135deg, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
              borderRadius: '12px',
              borderLeft: `4px solid ${theme.colors.primary}`,
            }}>
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                color: theme.colors.textPrimary,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                lineHeight: '1.6'
              }}>
                <span style={{ fontSize: '18px' }}>💡</span>
                <span>
                  <strong>Insight:</strong> Consistent daily activity leads to better learning outcomes. 
                  Students who maintain active streaks show 40% better retention rates!
                </span>
              </p>
            </div>
          </Card>
        </Section>
      )}

      {/* Time Distribution */}
      {data.time_distribution && data.time_distribution.by_subject && data.time_distribution.by_subject.length > 0 && (
        <Section>
          <SectionTitle>
            <FiClock />
            Time Distribution by Subject
          </SectionTitle>
          <Grid minWidth="400px">
            <Card>
              <ChartContainer height="400px">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={prepareTimeDistributionData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name}: ${safeToFixed(percentage, 0)}%`}
                      outerRadius={120}
                      fill={theme.colors.primary}
                      dataKey="hours"
                    >
                      {prepareTimeDistributionData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </Card>

            <Card>
              <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
                Subject Breakdown
              </h3>
              {prepareTimeDistributionData().map((subject, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    background: theme.colors.backgroundSecondary,
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: COLORS[index % COLORS.length],
                        }}
                      />
                      <span style={{ fontWeight: 600, color: theme.colors.textPrimary }}>
                        {subject.name}
                      </span>
                    </div>
                    <Badge color={COLORS[index % COLORS.length]}>
                      {safeToFixed(subject.percentage, 0)}%
                    </Badge>
                  </div>
                  <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                    {safeToFixed(subject.hours)} hours
                  </div>
                </div>
              ))}
            </Card>
          </Grid>
        </Section>
      )}

      {/* Session Analysis */}
      {data.session_analysis && (
        <Section>
          <SectionTitle>
            Study Sessions Analysis
          </SectionTitle>
          <Grid minWidth="250px">
            <StatCard>
              <StatLabel>Total Sessions</StatLabel>
              <StatValue color={theme.colors.primary}>
                {data.session_analysis.total_sessions}
              </StatValue>
              <StatDescription>
                in the last {data.period_days} days
              </StatDescription>
            </StatCard>

          <StatCard>
            <StatLabel>Average Duration</StatLabel>
            <StatValue color={theme.colors.info}>
              {safeToFixed(data.session_analysis.avg_duration_minutes, 0)} min
            </StatValue>
              <StatDescription>
                per study session
              </StatDescription>
            </StatCard>
          </Grid>

          {data.session_analysis.daily_sessions && data.session_analysis.daily_sessions.length > 0 && (
            <Card style={{ marginTop: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: theme.colors.textPrimary }}>
                Daily Study Duration
              </h3>
              <ChartContainer height="350px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={prepareSessionData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: theme.colors.textSecondary, fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fill: theme.colors.textSecondary }}
                      label={{
                        value: 'Minutes',
                        angle: -90,
                        position: 'insideLeft',
                        fill: theme.colors.textSecondary,
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="minutes"
                      stroke={theme.colors.primary}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      name="Duration (min)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            </Card>
          )}
        </Section>
      )}

      {/* Peak Times */}
      {data.peak_times && data.peak_times.length > 0 && (
        <Section>
          <SectionTitle>
            Peak Activity Times
          </SectionTitle>
          <Card>
            <p style={{ color: theme.colors.textSecondary, marginBottom: '16px' }}>
              Most active learning hours: <strong>{data.peak_hour}:00</strong>
            </p>
            <ChartContainer height="350px">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={preparePeakTimesData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
                  <XAxis
                    dataKey="hour"
                    tick={{ fill: theme.colors.textSecondary }}
                  />
                  <YAxis
                    tick={{ fill: theme.colors.textSecondary }}
                    label={{
                      value: 'Activity Count',
                      angle: -90,
                      position: 'insideLeft',
                      fill: theme.colors.textSecondary,
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="activity"
                    fill={theme.colors.secondary}
                    name="Activities"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          </Card>
        </Section>
      )}

      {/* Completion Rates */}
      {data.completion_rates && data.completion_rates.length > 0 && (
        <Section>
          <SectionTitle>
            Content Completion Rates
          </SectionTitle>
          <Grid minWidth="250px">
            {prepareCompletionData().map((item, index) => (
              <StatCard key={index}>
                <StatLabel>{item.name} Completion</StatLabel>
                <StatValue color={COLORS[index % COLORS.length]}>
                  {safeToFixed(item.rate)}%
                </StatValue>
                <StatDescription>
                  {item.completed} / {item.total} completed
                </StatDescription>
              </StatCard>
            ))}
          </Grid>
        </Section>
      )}
    </>
  );
};

export default EngagementMetrics;

